import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

export interface AssetClassFieldSchema {
  suggested?: {
    standortprofil?: string[];
    risikoklasse?: string[];
    investmentvolumen?: string[];
    objektqualitaet?: string[];
    walt?: string[];
    mieter_betreiber?: string[];
    esg_energie?: string[];
    deal_struktur?: string[];
    ausschluss?: string[];
  };
}

export interface PickerAssetClass {
  id: string;
  code: string;
  label_de: string;
  label_en: string;
  sort_order: number;
  field_schema?: AssetClassFieldSchema;
}

interface AssetClassPickerProps {
  value: string | null;
  /** Callback: id, label, vollständige Klasse (inkl. field_schema mit suggestions) */
  onChange: (id: string, label: string, ac?: PickerAssetClass) => void;
  /** IDs, die als "bereits verwendet" grau dargestellt werden (Profile pro Investor) */
  disabledIds?: string[];
  /** Inline statt Dropdown */
  variant?: 'select' | 'grid';
  required?: boolean;
}

/**
 * Pflicht-Auswahl der 34 Assetklassen (aktive). Konzept §4.4.
 * Liefert id + label_de zurück.
 */
export function AssetClassPicker({ value, onChange, disabledIds = [], variant = 'select', required }: AssetClassPickerProps) {
  const [classes, setClasses] = useState<PickerAssetClass[]>([]);

  useEffect(() => {
    apiClient.get<{ data: unknown; flat: PickerAssetClass[] }>('/asset-classes')
      .then(r => setClasses((r.data.flat ?? []).filter(c => c.label_de)))
      .catch(() => setClasses([]));
  }, []);

  if (variant === 'grid') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
        {classes.map(c => {
          const disabled = disabledIds.includes(c.id);
          const active = value === c.id;
          return (
            <button key={c.id} type="button" disabled={disabled}
              onClick={() => onChange(c.id, c.label_de, c)}
              title={disabled ? 'Bereits ein Profil dieser Klasse vorhanden' : c.label_de}
              style={{
                padding: '8px 10px', fontSize: 12, textAlign: 'left',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                background: active ? 'var(--accent)' : disabled ? 'var(--surface2)' : 'var(--surface)',
                color: active ? '#fff' : disabled ? 'var(--text3)' : 'var(--text)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                fontWeight: active ? 500 : 400,
              }}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, marginRight: 6, opacity: 0.6 }}>
                {String(c.sort_order).padStart(2, '0')}
              </span>
              {c.label_de}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <select required={required} value={value ?? ''}
      onChange={e => {
        const c = classes.find(x => x.id === e.target.value);
        if (c) onChange(c.id, c.label_de, c);
      }}
      style={{
        width: '100%', padding: '8px 12px',
        border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
        fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
      }}>
      <option value="" disabled>— Assetklasse wählen (Pflicht) —</option>
      {classes.map(c => (
        <option key={c.id} value={c.id} disabled={disabledIds.includes(c.id)}>
          {c.label_de}{disabledIds.includes(c.id) ? ' · bereits vorhanden' : ''}
        </option>
      ))}
    </select>
  );
}
